import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {select, Store} from '@ngrx/store';
import {SbbAutocomplete, SbbAutocompleteTrigger} from '@sbb-esta/lyne-angular/autocomplete';
import {SbbDialogRef} from '@sbb-esta/lyne-angular/dialog';
import {SbbFormField} from '@sbb-esta/lyne-angular/form-field';
import {SbbOptGroup, SbbOption, SbbOptionHint} from '@sbb-esta/lyne-angular/option';
import {OrtApiModel} from 'cargo-hub-ui-customer-api';
import {filter, map, switchMap} from 'rxjs/operators';
import {selectUser} from '../../auth/auth-store/auth.selectors';
import {FilterHelper} from '../../cd-common/helper/filter-helper';
import {OrtHelper} from '../../model/ort-helper';
import {UIC_SWITZERLAND} from '../../model/uic/uicLand';
import {UserHelper} from '../../model/user-helper';
import {User} from '../../services/auth/userEntity';
import {StationsService} from '../../services/stations/stations.service';
import {Iterator} from '../../util/iterator';
import {CustomEventQuickActionUsed} from '../../util/monitoring/custom-events';
import {MonitoringHelper} from '../../util/monitoring/monitoring-helper';

interface QuickAction {
  label: string;
  routerUrl: string;
}

interface QuickActionsGroup {
  label: string;
  actions: QuickAction[];
}

@UntilDestroy()
@Component({
  selector: 'app-quick-action-input',
  imports: [AsyncPipe, FormsModule, SbbAutocomplete, SbbAutocompleteTrigger, SbbFormField, SbbOptGroup, SbbOption, SbbOptionHint, ReactiveFormsModule],
  templateUrl: './quick-action-input.component.html',
  styleUrl: './quick-action-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuickActionInputComponent implements OnInit {
  public readonly groupLimit = 5;

  private router = inject(Router);
  private dialogRef = inject(SbbDialogRef);
  private store = inject(Store);
  private stationsService = inject(StationsService);

  private currentUser: User | null = null;
  private allStations: OrtApiModel[] = [];

  formControl = new FormControl<string | QuickAction>('', {nonNullable: true});
  quickActions$ = this.formControl.valueChanges.pipe(
    filter(v => typeof v === 'string'),
    map(value => this.getQuickActions(value.trim()))
  );
  displayOption: (value: QuickAction) => string = (value: QuickAction) => value.label;

  ngOnInit() {
    this.store.pipe(select(selectUser), untilDestroyed(this)).subscribe(user => (this.currentUser = user));
    this.store
      .pipe(
        select(selectUser),
        filter(user => !!user),
        untilDestroyed(this),
        switchMap(() => this.stationsService.getStationsOfCountry(UIC_SWITZERLAND, false))
      )
      .subscribe(stations => {
        this.allStations = stations;
      });
  }

  submit() {
    const option = this.formControl.value;
    if (!option || typeof option === 'string') {
      return;
    }
    this.dialogRef?.close();
    MonitoringHelper.monitorCustomEvent(new CustomEventQuickActionUsed(option.routerUrl, option.label));
    this.router.navigateByUrl(option.routerUrl);
  }

  private getQuickActions(inputValue: string): QuickActionsGroup[] {
    return [this.getNavigationActions(inputValue), this.getActionActions(inputValue)].filter(group => group.actions.length > 0);
  }

  private getNavigationActions(inputValue: string): QuickActionsGroup {
    return {
      label: $localize`:@@QUICK_ACTIONS_GROUP_NAVIGATION:Navigation`,
      actions: Iterator.from(this.getBestellungNavigationActions(inputValue))
        .concat(this.getStaticNavigationActions(inputValue))
        .concat(this.getWagendispoNavigationActions(inputValue))
        .take(this.groupLimit + 1)
        .toArray()
    };
  }

  private getActionActions(inputValue: string): QuickActionsGroup {
    const normalizedInputValue = FilterHelper.splitWords(inputValue);
    return {
      label: $localize`:@@QUICK_ACTIONS_GROUP_ACTIONS:Aktionen`,
      actions: [
        {
          label: $localize`:@@ORDER_LIST_NEW:Neue Bestellung` + ' - ' + $localize`:@@SELECT_ORDER_TEMPLATE_BUTTON:Auswahl kopieren...`,
          routerUrl: '/ebooking/order/list?create=selectTemplate&copyAsOrder=true'
        },
        {
          label: $localize`:@@SELECT_ORDER_WLV:Neue WLV Bestellung`,
          routerUrl: '/ebooking/order/list?create=createOrder&wlv=true'
        },
        {
          label: $localize`:@@SELECT_ORDER_BLOCK_TRAIN:Neue Ganzzug Bestellung`,
          routerUrl: '/ebooking/order/list?create=createOrder&wlv=false'
        },
        {
          label: $localize`:@@ORDER_TEMPLATES_LIST_NEW:Neues Muster` + ' - ' + $localize`:@@SELECT_ORDER_TEMPLATE_BUTTON:Auswahl kopieren...`,
          routerUrl: '/ebooking/order/templates?create=selectTemplate&copyAsOrder=false'
        },
        {
          label: $localize`:@@EMPTY_WAGON_ORDER_LIST_NEW:Neue Leerwagenbestellung`,
          routerUrl: '/ebooking/empty-wagon-orders/list?create-lwb=selectTemplate'
        }
      ].filter(action => FilterHelper.matchesAnd(normalizedInputValue, action.label))
    };
  }

  *getBestellungNavigationActions(inputValue: string): IterableIterator<QuickAction> {
    const normalizedInput = FilterHelper.normalize(inputValue);
    if (normalizedInput.match(/^b\d{9}$/)) {
      yield {label: $localize`:@@ORDER_PROCESS_TITLE:Bestellung` + ' ' + inputValue, routerUrl: `/ebooking/order/list?order=${normalizedInput.toLocaleUpperCase()}`};
    }
    if (normalizedInput.match(/^mb\d{8}$/)) {
      yield {label: $localize`:@@LINK_ORDER_TEMPLATES:Musterbestellung` + ' ' + inputValue, routerUrl: `/ebooking/order/templates?order=${normalizedInput.toLocaleUpperCase()}`};
    }
    // both auftrag nummer and leerwagenbestellung nummer are 9 digits long
    if (inputValue.match(/^\d{2}-\d{3}-\d{3}-\d$/) || inputValue.match(/^\d{9}$/)) {
      yield {label: $localize`:@@ASSIGNMENT_EDIT_ASSIGNMENT:Auftrag` + ' ' + inputValue, routerUrl: `/ebooking/transports/list?transport=${normalizedInput}`};
    }
    if (inputValue.match(/^\d{3}-\d{3}-\d{3}$/)) {
      yield {label: $localize`:@@ORDER_EMPTY_WAGON_EDITOR_TITLE:Leerwagenbestellung` + ' ' + inputValue, routerUrl: `/ebooking/empty-wagon-orders/list?emptyWagonOrderId=${normalizedInput}`};
    }
  }

  getWagendispoNavigationActions(inputValue: string): IterableIterator<QuickAction> {
    return Iterator.from(this.allStations)
      .map(s =>
        Iterator.from([
          {
            label: $localize`:@@LINK_WAGON_SCHEDULING_L2:Wagendisposition` + ': ' + $localize`:@@WAGON_SCHEDULING_LINK_DESTINATION:Wagen im Empfang` + ' - ' + s.name + ' (' + s.uicNummer + ')',
            routerUrl: `/ebooking/wagon-scheduling/in/${OrtHelper.stationId(s)}`
          },
          {
            label: $localize`:@@LINK_WAGON_SCHEDULING_L2:Wagendisposition` + ': ' + $localize`:@@WAGON_SCHEDULING_LINK_ORIGIN:Wagen im Versand` + ' - ' + s.name + ' (' + s.uicNummer + ')',
            routerUrl: `/ebooking/wagon-scheduling/out/${OrtHelper.stationId(s)}`
          }
        ])
      )
      .flatten()
      .filter(action => FilterHelper.matchesAnd(FilterHelper.splitWords(inputValue), action.label));
  }

  getStaticNavigationActions(inputValue: string): IterableIterator<QuickAction> {
    const isSupportUser = UserHelper.isSupportUser(this.currentUser);
    const hasCockpitRole = UserHelper.isStorageCockpitUser(this.currentUser) || isSupportUser;
    const isCisoUser = UserHelper.isCisoUser(this.currentUser);
    const isAdminExtern = UserHelper.isUserAdminExtern(this.currentUser);
    const isAdminIntern = UserHelper.isUserAdminIntern(this.currentUser);
    const normalizedInputValue = FilterHelper.splitWords(inputValue);
    const isCostsOverviewUser = UserHelper.isCostsOverviewUser(this.currentUser);
    return Iterator.from([
      // Top Level:
      {label: $localize`:@@NAV_HOME:Home`, routerUrl: '/'},
      {label: $localize`:@@NAV_ORDER:Cargo eBooking`, routerUrl: '/ebooking/order/list'},
      {label: $localize`:@@NAV_SCHEDULE:Cargo WLV Fahrplan`, routerUrl: '/schedule'},
      {label: $localize`:@@NAV_STORAGE_COCKPIT:Cargo Storage Cockpit`, routerUrl: '/storage-cockpit'},
      {label: $localize`:@@NAV_PUSH:Cargo Push`, routerUrl: '/push'},
      {label: $localize`:@@NAV_PROFILE_SETTINGS:Einstellungen`, routerUrl: '/settings'},
      {label: $localize`:@@NAV_USER_COCKPIT:Benutzerverwaltung`, routerUrl: '/user-cockpit'},
      {label: $localize`:@@NAV_INTERNAL_USER_COCKPIT:Interne Benutzerverwaltung`, routerUrl: '/internal-user-cockpit'},
      {label: $localize`:@@STOERUNGSMELDUNGEN_NAV_TITLE:Störungsmeldungen`, routerUrl: '/disruption-alerts'},
      {label: $localize`:@@NAV_RELEASE_NOTES:Release Informationen`, routerUrl: '/release-notes'},
      {label: $localize`:@@TIPPS_NAV_TITLE:Einführung neuer Buchungsprozess`, routerUrl: '/tips'},
      {label: $localize`:@@NAV_CHANGE_PASSWORD: Passwort ändern `.trim(), routerUrl: '/settings/password'},
      {label: $localize`:@@NOTIFICATION_HEADLINE:Meldungen`, routerUrl: this.router.url.includes('?') ? `${this.router.url}&notifications=true` : `${this.router.url}?notifications=true`},
      {label: $localize`:@@NAV_COSTS_OVERVIEW:Kostenübersicht`, routerUrl: '/costs-overview'},
      // ebooking:
      {label: $localize`:@@LINK_ORDERS:Bestellungen`, routerUrl: '/ebooking/order/list'},
      {label: $localize`:@@ORDER_LIST_FINISHED:Abgeschlossene Bestellungen`, routerUrl: '/ebooking/order/list-finished'},
      {label: $localize`:@@ORDERS:Wochenbestellung`, routerUrl: '/ebooking/order/reservation'},
      {label: $localize`:@@LINK_ORDER_TEMPLATES:Musterbestellung`, routerUrl: '/ebooking/order/templates'},
      {label: $localize`:@@ASSIGNMENTS:Aufträge`, routerUrl: '/ebooking/transports/list'},
      {label: $localize`:@@ASSIGNMENT_LIST_FINISHED:Abgeschlossene Aufträge`, routerUrl: '/ebooking/transports/list-finished'},
      {label: $localize`:@@LINK_WAGON_SCHEDULING_L2:Wagendisposition` + ': ' + $localize`:@@WAGON_SCHEDULING_LINK_DESTINATION:Wagen im Empfang`, routerUrl: '/ebooking/wagon-scheduling/in'},
      {label: $localize`:@@LINK_WAGON_SCHEDULING_L2:Wagendisposition` + ': ' + $localize`:@@WAGON_SCHEDULING_LINK_ORIGIN:Wagen im Versand`, routerUrl: '/ebooking/wagon-scheduling/out'},
      {label: $localize`:@@LINK_WAGON_SCHEDULING_L2:Wagendisposition` + ': ' + $localize`:@@WAGON_SCHEDULING_LINK_DELIVERED:Zugestellte Wagen`, routerUrl: `/ebooking/wagon-scheduling/delivered`},
      {label: $localize`:@@LINK_WAGON_SCHEDULING_L2:Wagendisposition` + ': ' + $localize`:@@WAGON_SCHEDULING_LINK_PARKED:Abgestellte Wagen`, routerUrl: `/ebooking/wagon-scheduling/parked`},
      {label: $localize`:@@EMPTY_WAGGON_ORDER_LIST:Leerwagenbestellungen`, routerUrl: '/ebooking/empty-wagon-orders/list'},
      {label: $localize`:@@EMPTY_WAGGON_ORDER_LIST_FINISHED:Abgeschlossene Leerwagenbestellungen`, routerUrl: '/ebooking/empty-wagon-orders/list-finished'},
      // storage cockpit:
      {label: $localize`:@@STORAGE_TITLE:Storage` + ' ' + $localize`:@@STORAGE_COCKPIT_MENU_INVOICE_CORRECTIONS:Rechnungskorrekturen`, routerUrl: '/storage-cockpit/billing'},
      {label: $localize`:@@STORAGE_TITLE:Storage` + ' ' + $localize`:@@STORAGE_COCKPIT_MENU_CUSTOMER_DATA:Kundendaten`, routerUrl: '/storage-cockpit/customer'},
      {label: $localize`:@@STORAGE_TITLE:Storage` + ' ' + $localize`:@@STORAGE_COCKPIT_MENU_WAGON_DATA:Wagendaten`, routerUrl: '/storage-cockpit/waggons'},
      {label: $localize`:@@STORAGE_TITLE:Storage` + ' ' + $localize`:@@STORAGE_COCKPIT_MENU_WAGON_DATA_LIST:Festzugeteilte Wagen`, routerUrl: '/storage-cockpit/waggons/list'},
      {label: $localize`:@@STORAGE_TITLE:Storage` + ' ' + $localize`:@@STORAGE_COCKPIT_MENU_WAGON_DATA_EDITOR:Kostenbefreite Wagen`, routerUrl: '/storage-cockpit/waggons/edit'}
    ]).filter(action => {
      if (!hasCockpitRole && action.routerUrl.startsWith('/storage-cockpit')) {
        return false;
      }
      if (!isCisoUser && action.routerUrl === '/settings/password') {
        return false;
      }
      if (!isAdminExtern && !isSupportUser && action.routerUrl === '/user-cockpit') {
        return false;
      }
      if (!isAdminIntern && !isSupportUser && action.routerUrl === '/internal-user-cockpit') {
        return false;
      }
      if (!isCostsOverviewUser && !isSupportUser && action.routerUrl.startsWith('/costs-overview')) {
        return false;
      }
      return FilterHelper.matchesAnd(normalizedInputValue, action.label);
    });
  }
}
